import type { TourStep } from "./AppTutorial";

// Einführungs-Tour für den Manager-Bereich (zweisprachig wie der ganze
// Mitarbeiterbereich). Erklärt die Kacheln von /manager über data-tour="mgr-*"
// und die Menüpunkte über data-tour="nav-<letztes Pfadsegment>".
export const MANAGER_TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: { de: "Willkommen im Manager-Bereich", en: "Welcome to the manager area" },
    body: [
      {
        de: "Hier siehst du, wie weit dein Team mit seinen Schulungen ist – und wer noch etwas offen hat.",
        en: "Here you can see how far your team is with its training – and who still has something open.",
      },
      {
        de: "Die Tour zeigt dir in einer Minute, wo du was findest.",
        en: "This tour shows you where everything is in about a minute.",
      },
    ],
  },
  {
    id: "nav",
    target: '[data-tour="nav"]',
    sidebar: true,
    title: { de: "Die Navigation", en: "Navigation" },
    body: [
      {
        de: "Links findest du die Bereiche. Am Computer klappt die Leiste auf, sobald du mit der Maus darüberfährst – auf dem Handy öffnest du sie über das Menü-Symbol oben links.",
        en: "The areas are on the left. On a computer the bar expands when you hover over it – on a phone, open it with the menu icon at the top left.",
      },
    ],
  },
  {
    id: "team",
    target: '[data-tour="nav-manager"]',
    sidebar: true,
    title: { de: "Mein Team", en: "My team" },
    body: [
      {
        de: "Der Stand deines Teams auf einen Blick: Abschlussquote, Frist und jede Person mit ihren offenen Modulen. Schauen wir uns die Seite kurz an.",
        en: "Your team's status at a glance: completion rate, deadline and every person with their open modules. Let's take a quick look at the page.",
      },
    ],
  },
  {
    id: "mgr-overall",
    target: '[data-tour="mgr-overall"]',
    optional: true,
    title: { de: "Gesamtfortschritt", en: "Overall progress" },
    body: [
      {
        de: "Wie viele der zugewiesenen Module dein Team erledigt hat. Abgelaufene Abschlüsse zählen nicht mit, Personen ohne Schulungspflicht auch nicht.",
        en: "How many of the assigned modules your team has completed. Expired completions don't count, and neither do people without a training duty.",
      },
    ],
  },
  {
    id: "mgr-deadline",
    target: '[data-tour="mgr-deadline"]',
    optional: true,
    title: { de: "Frist fürs Team", en: "Team deadline" },
    body: [
      {
        de: "Bis wann alle offenen Module erledigt sein sollen – und wie viele Tage bis dahin bleiben.",
        en: "By when all open modules should be done – and how many days are left until then.",
      },
    ],
  },
  {
    id: "mgr-members",
    target: '[data-tour="mgr-members"]',
    optional: true,
    title: { de: "Stand je Person", en: "Status per person" },
    body: [
      {
        de: "Jede Person aus deinem Team mit ihrem eigenen Fortschritt. Von hier kannst du an offene Module erinnern, sie dir anzeigen lassen und festlegen, wer gerade nicht teilnehmen muss.",
        en: "Every person on your team with their own progress. From here you can send reminders about open modules, list them and set who does not have to take part right now.",
      },
    ],
  },
  {
    id: "mgr-remind-all",
    target: '[data-tour="mgr-remind-all"]',
    optional: true,
    title: { de: "Alle Offenen auf einmal erinnern", en: "Remind everyone at once" },
    body: [
      {
        de: "Ein Klick sammelt alle, die noch etwas offen haben. Du siehst die Liste, nimmst einzelne heraus und schickst dann eine Erinnerung an alle.",
        en: "One click collects everyone who still has something open. You see the list, untick individuals and then send one reminder to all of them.",
      },
    ],
  },
  {
    id: "mgr-co",
    target: '[data-tour="mgr-co"]',
    optional: true,
    title: { de: "Co-Manager", en: "Co-managers" },
    body: [
      {
        de: "Trag hier deine Vertretung ein. Ein Co-Manager sieht dein Team so wie du, darf erinnern und bekommt die monatliche Team-Mail.",
        en: "Add your deputy here. A co-manager sees your team the way you do, can send reminders and receives the monthly team email.",
      },
    ],
  },
  {
    id: "mgr-wish",
    target: '[data-tour="mgr-wish"]',
    optional: true,
    title: { de: "Dir fehlt eine Schulung?", en: "Missing a training?" },
    body: [
      {
        de: "Wünsch dir hier ein neues Thema für dein Team. Der Wunsch geht per E-Mail aus deinem Postfach an SHERLOX, die Antwort kommt direkt zu dir.",
        en: "Request a new topic for your team here. The request is emailed from your mailbox to SHERLOX, and the reply comes straight to you.",
      },
    ],
  },
  {
    id: "reports",
    target: '[data-tour="nav-berichte"]',
    sidebar: true,
    title: { de: "Berichte & Nachweise", en: "Reports & records" },
    body: [
      {
        de: "Der Nachweis für ein Audit hat eine eigene Seite: Schulungsquote, Stand je Modul und je Person – mit Datum und Uhrzeit jedes Abschlusses.",
        en: "The record for an audit has its own page: completion rate, status per module and per person – with the date and time of each completion.",
      },
      {
        de: "Dort filterst du nach einem Modul, exportierst den Nachweis als Excel-Datei oder druckst ihn.",
        en: "There you can filter by module, export the record as an Excel file or print it.",
      },
    ],
  },
  {
    id: "mgr-report-kpis",
    target: '[data-tour="mgr-report-kpis"]',
    optional: true,
    title: { de: "Kennzahlen", en: "Key figures" },
    body: [
      {
        de: "Schulungsquote, vollständig geschulte Personen und offene Module deines Teams. Wer nicht teilnehmen muss, steht im Bericht, zählt aber in keiner Quote. Wer sich noch nie angemeldet hat, zählt mit, sobald er in einer Gruppe ist.",
        en: "Completion rate, fully trained people and open modules for your team. Anyone without a training requirement appears in the report but is not counted in any rate. People who have never signed in are counted once they belong to a group.",
      },
    ],
  },
  {
    id: "mgr-report-table",
    target: '[data-tour="mgr-report-table"]',
    optional: true,
    title: { de: "Nach Modul oder Person", en: "By module or person" },
    body: [
      {
        de: "Ein Klick auf ein Modul zeigt, wer es erledigt hat und wer nicht. Unter „Nach Person“ öffnest du je Person die Module mit Datum und Uhrzeit des Abschlusses.",
        en: "Click a module to see who has completed it and who has not. Under “By person” you can open each person's modules with the date and time of completion.",
      },
    ],
  },
  {
    id: "employee-view",
    target: '[data-tour="nav-employee"]',
    sidebar: true,
    title: { de: "Zurück zu deinen Schulungen", en: "Back to your own training" },
    body: [
      {
        de: "Hier geht es zurück in deinen eigenen Bereich mit Dashboard und Trainings.",
        en: "This takes you back to your own area with your dashboard and training.",
      },
    ],
  },
  {
    id: "theme",
    target: '[data-tour="theme"]',
    title: { de: "Hell oder dunkel", en: "Light or dark" },
    body: [
      {
        de: "Mit diesem Schalter wechselst du jederzeit zwischen hellem und dunklem Design.",
        en: "Use this switch to toggle between light and dark design at any time.",
      },
    ],
  },
  {
    id: "help",
    target: '[data-tour="help"]',
    title: { de: "Hilfe jederzeit", en: "Help anytime" },
    body: [
      {
        de: "Über das Fragezeichen öffnest du diese Tour jederzeit wieder.",
        en: "Click the question mark to open this tour again at any time.",
      },
    ],
  },
  {
    id: "finish",
    title: { de: "Wer hat noch etwas offen?", en: "Who still has something open?" },
    body: [
      {
        de: "Am schnellsten siehst du das in der Liste deiner Mitarbeiter.",
        en: "The quickest way to see that is the list of your team members.",
      },
    ],
  },
];
